/**
 * ชั้นติดต่อ IndexedDB ของคิวคำสั่งออฟไลน์ (M11)
 *
 * ใช้ IndexedDB API ของเบราว์เซอร์ตรงๆ ไม่มี wrapper library — `apps/web/package.json`
 * อยู่นอก write scope และคิวนี้ต้องไม่เพิ่ม runtime dependency
 *
 * ทุกทางเข้า IndexedDB ล้มได้: private window, storage ถูกบล็อก, quota เต็ม
 * หรือแท็บอื่นถือ schema เก่าอยู่จน upgrade ถูกบล็อก — ทุกกรณีต้องกลายเป็น
 * `OfflineQueueError` ที่บอกสาเหตุ ไม่ใช่ throw ดิบๆ และ **ห้าม** ตีความว่า
 * "อ่านไม่ได้" เท่ากับ "คิวว่าง"
 */

import { OfflineQueueError, type OfflineQueueErrorReason } from './commandEnvelope';

export const OFFLINE_QUEUE_DB_NAME = 'cap-erp-offline';
export const OFFLINE_QUEUE_DB_VERSION = 1;

/** ชื่อ store ตรงกับตารางฝั่ง server เพื่อให้ตามรอยได้ตอน debug หน้างาน */
export const COMMAND_STORE = 'offline_sync_command';
export const META_STORE = 'queue_meta';

/** index เรียงตาม device_sequence_no — unique เพื่อกันเลขซ้ำที่ระดับฐานข้อมูล */
export const DEVICE_SEQUENCE_INDEX = 'by_device_sequence_no';
export const SEQUENCE_COUNTER_KEY = 'last_allocated_device_sequence_no';

export function resolveIndexedDbFactory(): IDBFactory | null {
  if (typeof globalThis === 'undefined') {
    return null;
  }
  const factory = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  return factory ?? null;
}

export function requireIndexedDbFactory(): IDBFactory {
  const factory = resolveIndexedDbFactory();
  if (factory === null) {
    throw new OfflineQueueError(
      'INDEXEDDB_UNSUPPORTED',
      'เบราว์เซอร์นี้ไม่เปิดให้ใช้ IndexedDB — คิวออฟไลน์ใช้งานไม่ได้',
    );
  }
  return factory;
}

function createSchema(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(COMMAND_STORE)) {
    const store = db.createObjectStore(COMMAND_STORE, { keyPath: 'client_event_id' });
    store.createIndex(DEVICE_SEQUENCE_INDEX, 'device_sequence_no', { unique: true });
  }
  if (!db.objectStoreNames.contains(META_STORE)) {
    db.createObjectStore(META_STORE);
  }
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(OFFLINE_QUEUE_DB_NAME, OFFLINE_QUEUE_DB_VERSION);
    } catch (cause) {
      reject(
        new OfflineQueueError('OPEN_FAILED', 'เปิดฐานข้อมูลคิวออฟไลน์ไม่สำเร็จ', { cause }),
      );
      return;
    }

    let settled = false;

    request.onupgradeneeded = () => {
      createSchema(request.result);
    };

    /*
     * `blocked` = แท็บอื่นยังถือ connection เวอร์ชันเก่าอยู่ ฐานข้อมูลจึงยัง
     * upgrade ไม่ได้ ตอบกลับทันทีว่าใช้งานไม่ได้ ดีกว่าค้างรอเงียบๆ ถ้าแท็บนั้น
     * ปิดทีหลังแล้ว `success` มาถึง ให้ปิด connection ทิ้งเพราะมีคนตอบไปแล้ว
     */
    request.onblocked = () => {
      if (settled) {
        return;
      }
      settled = true;
      reject(
        new OfflineQueueError(
          'UPGRADE_BLOCKED',
          'มีแท็บอื่นเปิดคิวออฟไลน์เวอร์ชันเก่าค้างอยู่ — ปิดแท็บนั้นแล้วลองใหม่',
        ),
      );
    };

    request.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      reject(
        new OfflineQueueError('OPEN_FAILED', 'เปิดฐานข้อมูลคิวออฟไลน์ไม่สำเร็จ', {
          cause: request.error,
        }),
      );
    };

    request.onsuccess = () => {
      const db = request.result;
      if (settled) {
        db.close();
        return;
      }
      settled = true;
      resolve(db);
    };
  });
}

let cachedFactory: IDBFactory | null = null;
let cachedConnection: Promise<IDBDatabase> | null = null;

/**
 * เหตุผลของการเขียนล้มเหลวครั้งล่าสุดที่ยังไม่หาย
 *
 * คิวที่อ่านได้แต่ "เขียนไม่ได้" (เช่น quota เต็ม) ไม่ใช่คิวที่สุขภาพดี —
 * ผู้ปฏิบัติงานต้องรู้ก่อนจะสแกนใบต่อไป ไม่ใช่เห็นตัวเลขสวยๆ แล้วสแกนต่อ
 * ค่านี้หายเมื่อเขียนสำเร็จอีกครั้ง หรือเมื่อ IndexedDB factory เปลี่ยนตัว
 */
let writeFailureReason: OfflineQueueErrorReason | null = null;

function invalidateConnection(): void {
  cachedFactory = null;
  cachedConnection = null;
  writeFailureReason = null;
}

export function recordWriteFailure(reason: OfflineQueueErrorReason): void {
  writeFailureReason = reason;
}

export function clearWriteFailure(): void {
  writeFailureReason = null;
}

export function getWriteFailureReason(): OfflineQueueErrorReason | null {
  return writeFailureReason;
}

/**
 * คืน connection ที่ใช้ร่วมกันทั้งแอป
 *
 * cache ผูกกับตัว `IDBFactory` ที่ใช้เปิด ถ้า global เปลี่ยน (เช่นเบราว์เซอร์
 * คืนสิทธิ์ storage หรือ test สลับ implementation) cache จะถูกทิ้งเองโดยไม่ต้อง
 * มี API สำหรับ reset แยกต่างหาก
 */
export function getQueueConnection(): Promise<IDBDatabase> {
  let factory: IDBFactory;
  try {
    factory = requireIndexedDbFactory();
  } catch (error) {
    invalidateConnection();
    return Promise.reject(error);
  }

  if (factory !== cachedFactory) {
    invalidateConnection();
  }

  if (cachedConnection === null) {
    cachedFactory = factory;
    cachedConnection = openDatabase(factory).then(
      (db) => {
        /* แท็บอื่นขอ upgrade: ต้องปล่อย connection ไม่งั้นเราเองจะเป็นตัวบล็อก */
        db.onversionchange = () => {
          db.close();
          invalidateConnection();
        };
        db.onclose = () => {
          invalidateConnection();
        };
        return db;
      },
      (error: unknown) => {
        invalidateConnection();
        throw error;
      },
    );
  }

  return cachedConnection;
}

/**
 * ปิด connection ที่ถืออยู่
 *
 * ใช้เมื่อแท็บอื่นขอ upgrade schema หรือเมื่อต้องการปล่อยทรัพยากรตอนหน้าจอถูกซ่อน
 * ครั้งถัดไปที่มีการอ่าน/เขียน คิวจะเปิด connection ใหม่ให้เอง
 */
export async function closeQueueConnection(): Promise<void> {
  const pending = cachedConnection;
  invalidateConnection();
  if (pending === null) {
    return;
  }
  try {
    (await pending).close();
  } catch {
    /* เปิดไม่สำเร็จตั้งแต่แรก — ไม่มี connection ให้ปิด */
  }
}

export function isQuotaError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'QuotaExceededError'
  );
}

export function toWriteError(error: unknown): OfflineQueueError {
  if (error instanceof OfflineQueueError) {
    return error;
  }
  if (isQuotaError(error)) {
    return new OfflineQueueError(
      'QUOTA_EXCEEDED',
      'พื้นที่เก็บข้อมูลในเครื่องเต็ม — บันทึกคำสั่งลงคิวไม่สำเร็จ',
      { cause: error },
    );
  }
  return new OfflineQueueError('WRITE_FAILED', 'บันทึกคำสั่งลงคิวออฟไลน์ไม่สำเร็จ', {
    cause: error,
  });
}

export function toReadError(error: unknown): OfflineQueueError {
  if (error instanceof OfflineQueueError) {
    return error;
  }
  return new OfflineQueueError('READ_FAILED', 'อ่านคิวออฟไลน์ไม่สำเร็จ', { cause: error });
}

/**
 * เปิด readonly transaction แล้วคืนผลของ request เดียว
 *
 * ตั้งใจไม่ทำ `await` คั่นกลางระหว่าง request ในธุรกรรมเดียวกัน เพราะ IndexedDB
 * จะ auto-commit ถ้าปล่อยให้ event loop ว่างโดยไม่มี request ค้างอยู่
 */
/**
 * เปิด readwrite transaction แล้วคืนผล **หลังธุรกรรม commit จริง**
 *
 * `operation` ยิง request ที่ต้องการแล้วคืน thunk ของผลลัพธ์ ซึ่งจะถูกเรียกก็
 * ต่อเมื่อ `oncomplete` เกิดขึ้นเท่านั้น — ธุรกรรมที่ abort กลางคันจึงไม่มีทาง
 * ถูกรายงานว่าสำเร็จ (สถานะคำสั่งที่รายงานผิดคือคำสั่งที่หายไปเงียบ ๆ)
 *
 * ข้อบังคับเดียวกับ `readFromStore`: ห้าม `await` คั่นระหว่าง request ใน
 * ธุรกรรมเดียวกัน เพราะ IndexedDB auto-commit เมื่อ event loop ว่าง
 */
export function writeToStore<T>(
  db: IDBDatabase,
  storeName: string,
  operation: (store: IDBObjectStore, fail: (cause: unknown) => void) => () => T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let transaction: IDBTransaction;
    try {
      transaction = db.transaction(storeName, 'readwrite');
    } catch (cause) {
      reject(toWriteError(cause));
      return;
    }

    let settled = false;
    const fail = (cause: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;
      reject(toWriteError(cause));
    };

    let resultOf: () => T;
    try {
      resultOf = operation(transaction.objectStore(storeName), fail);
    } catch (cause) {
      fail(cause);
      return;
    }

    transaction.oncomplete = () => {
      if (settled) {
        return;
      }
      settled = true;
      try {
        resolve(resultOf());
      } catch (cause) {
        reject(toWriteError(cause));
      }
    };
    transaction.onabort = () => {
      fail(transaction.error);
    };
    transaction.onerror = () => {
      fail(transaction.error);
    };
  });
}

export function readFromStore<T>(
  db: IDBDatabase,
  storeName: string,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let transaction: IDBTransaction;
    try {
      transaction = db.transaction(storeName, 'readonly');
    } catch (cause) {
      reject(toReadError(cause));
      return;
    }

    let result: T;
    let settled = false;

    const request = operation(transaction.objectStore(storeName));
    request.onsuccess = () => {
      result = request.result;
    };
    request.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      reject(toReadError(request.error));
    };

    transaction.oncomplete = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(result);
    };
    transaction.onabort = () => {
      if (settled) {
        return;
      }
      settled = true;
      reject(toReadError(transaction.error));
    };
    transaction.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      reject(toReadError(transaction.error));
    };
  });
}
